import { produce } from 'immer'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import {
    GreetingMessage,
    Message,
    PlaceholderMessage,
    SuggestedMessages,
} from './message'

export const ChatStorePlaceholderKey = 'askDelaChatStore-placeholder'
export const ChatStoreRetentionMinsKey = 'askDelaChatRetentionMins'

/**
 * Represents the state of the chat messages in the chatbot.
 * This interface is used for state management in the chatbot.
 */
export interface ChatMessageState {
    /**
     * Indicates whether the chat messages are currently being loaded.
     */
    isLoading: boolean

    /**
     * Starts the loading process for the chat messages.
     */
    startLoading: () => void

    /**
     * Stops the loading process for the chat messages.
     */
    stopLoading: () => void

    /**
     * An array of chat messages.
     */
    messages: Message[]

    /**
     * Sets the chat messages to the specified array of messages.
     * @param messages - The array of messages to set.
     */
    setMessages: (messages: Message[]) => void

    /**
     * Adds a new message to the chat messages.
     * @param message - The message to add.
     */
    addMessage(message: Message): void

    /**
     * Removes the chat messages that match the specified predicate.
     * @param predicate - The predicate function used to determine which messages to remove.
     */
    removeMessages(predicate: (value: Message) => boolean | undefined): void

    /**
     * Sets the feedback for the specified message.
     * @param message - The message to set the feedback for.
     * @param isLiked - Indicates whether the message is liked or not.
     */
    setMessageFeedback: (message: Message, isLiked: boolean) => void

    /**
     * The abort controller used to cancel asking a message.
     */
    askMessageAborter?: AbortController

    /**
     * Creates a new abort controller to cancel asking a message.
     * @returns A new abort controller to cancel asking a message.
     */
    createAskMessageAborter: () => AbortController

    /**
     * Limits the number of responses in the chat messages.
     */
    limitResponses: () => void

    /**
     * Cleans up old messages based on the retention policy.
     * Retains the greeting and suggested messages.
     */
    cleanupOldMessages: () => void

    /**
     * Indicates whether the disclaimer is open.
     */
    isDisclaimerOpen: boolean

    /**
     * Sets whether the disclaimer is open.
     * @param state a boolean value indicating whether the disclaimer is open.
     */
    setIsDisclaimerOpen: (state: boolean) => void
}

export const createChatMessageStore = (chatInstanceId: string) =>
    create<ChatMessageState>()(
        persist(
            (set, get) => ({
                isLoading: false,
                startLoading: () => {
                    set(() => ({ isLoading: true }))

                    // add a placeholder message to indicate loading
                    get().addMessage(PlaceholderMessage)
                },
                stopLoading: () => {
                    set(() => ({ isLoading: false }))

                    // remove the placeholder messages
                    get().removeMessages((m) => m.isPlaceholder)
                },

                messages: [GreetingMessage, ...SuggestedMessages],
                setMessages: (messages: Message[]) => set({ messages }),
                addMessage: (message: Message) =>
                    set((state) => ({
                        messages: [...state.messages, message],
                    })),
                removeMessages: (predicate) => {
                    set((state) => ({
                        messages: state.messages.filter((m) => !predicate(m)),
                    }))
                },
                setMessageFeedback: (
                    agentMessage: Message,
                    isLiked: boolean
                ) => {
                    set(
                        produce((state) => ({
                            messages: state.messages.map((m: Message) =>
                                m.id === agentMessage.id
                                    ? {
                                          ...m,
                                          isLiked,
                                      }
                                    : m
                            ),
                        }))
                    )
                },

                createAskMessageAborter: () => {
                    const aborter = new AbortController()
                    set(() => ({ askMessageAborter: aborter }))
                    return aborter
                },

                limitResponses: () => {
                    const maxResponseMessages = 25
                    const agentMessages = get().messages.filter(
                        (x) => x.role === 'agent'
                    )

                    if (agentMessages.length > maxResponseMessages) {
                        // calculate the number of messages to skip
                        const skipCount =
                            agentMessages.length - maxResponseMessages

                        // finds the oldest messages in the limited number of messages
                        const oldestInLimitedMessages = agentMessages
                            .slice(skipCount)
                            .reduce((a, b) =>
                                new Date(a.dateCreated) <
                                new Date(b.dateCreated)
                                    ? a
                                    : b
                            )

                        // remove messages older than the oldest in the limited messages to maintain our max # of messages
                        get().removeMessages(
                            (m) =>
                                m.dateCreated <
                                oldestInLimitedMessages.dateCreated
                        )
                    }
                },

                cleanupOldMessages: () => {
                    const maxMins = 10080 // 7 days
                    const retentionMins = parseInt(
                        localStorage.getItem(ChatStoreRetentionMinsKey) ||
                            maxMins.toString(),
                        10
                    )

                    // bounds check - 1 <= retentionMins <= maxMins
                    const minsToKeep = Math.max(
                        1,
                        Math.min(retentionMins, maxMins)
                    )

                    // save the new value to local storage, also handles first load
                    localStorage.setItem(
                        ChatStoreRetentionMinsKey,
                        minsToKeep.toString()
                    )
                    const now = Date.now()
                    const cutoffTime = now - minsToKeep * 60 * 1000 // convert to milliseconds

                    // cleanup messages older than the cutoff time
                    // retain the greeting and suggestions
                    get().removeMessages(
                        (m) =>
                            new Date(m.dateCreated).getTime() < cutoffTime &&
                            !m.isGreeting &&
                            m.role !== 'suggested'
                    )
                },

                isDisclaimerOpen: false,
                setIsDisclaimerOpen: (state: boolean) => {
                    set(() => ({ isDisclaimerOpen: state }))
                },
            }),
            {
                name: chatInstanceId,
                storage: createJSONStorage(() => localStorage),
            }
        )
    )

/**
 * Factory function to create a dynamic chat message stores
 * Based on: https://zustand.docs.pmnd.rs/hooks/use-store#using-dynamic-scoped-(non-global)-vanilla-stores-in-react
 */
export const createChatMessageStoreFactory = (
    chatMessageStores: Map<string, ReturnType<typeof createChatMessageStore>>
) => {
    return (chatInstanceId: string) => {
        // Find the chat instance in the map. Create one if it doesn't exist.
        if (!chatMessageStores.has(chatInstanceId)) {
            chatMessageStores.set(
                chatInstanceId,
                createChatMessageStore(chatInstanceId)
            )
        }

        return chatMessageStores.get(chatInstanceId)!
    }
}
